import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Editorial pull-quote for Hebrew RTL content.
 *
 * Apple/ShadCN-style: large italic body, a single subtle decorative
 * quotation mark in muted stone, and an optional attribution line in
 * smaller muted text below. Refrained from any tinted box — this is meant
 * to feel like a typographic pause inside a long-form article, not a
 * highlighted callout.
 *
 * Use sparingly; one per section at most.
 */
export type PullQuoteProps = {
  /** The quoted body. Accepts plain text or rich nodes. */
  children: ReactNode;
  /** Optional attribution rendered below the quote in muted text. */
  attribution?: string;
  /** Extra classes merged into the outer figure. */
  className?: string;
};

export function PullQuote({ children, attribution, className }: PullQuoteProps) {
  return (
    <figure
      className={cn(
        "relative my-6 ps-6 pe-2 py-2",
        // Subtle inline-start rule that reads correctly in RTL via logical props.
        "border-s border-stone-200/80",
        className,
      )}
    >
      {/* Decorative quotation mark — soft stone, large, sits to the start. */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none select-none absolute -top-2 start-2",
          "text-5xl leading-none font-serif text-stone-300/80",
        )}
      >
        &#8221;
      </span>

      <blockquote
        className={cn(
          "relative text-lg sm:text-xl leading-[1.8] italic",
          "text-stone-800 font-normal tracking-tight",
        )}
      >
        {children}
      </blockquote>

      {attribution ? (
        <figcaption
          className={cn(
            "mt-3 text-sm text-stone-500 not-italic",
            "before:content-['—'] before:me-2 before:text-stone-400",
          )}
        >
          {attribution}
        </figcaption>
      ) : null}
    </figure>
  );
}
